'use client';

import { useEffect, useRef, useState } from 'react';
import ParticleCanvas from '../home/components/ParticleCanvas';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';

interface LegalLink {
  title: string;
  description: string;
  href: string;
  icon: string;
}

const legalLinks: LegalLink[] = [
  {
    title: 'Privacy Policy',
    description: 'Learn how VantaRust collects and protects your data.',
    href: '/privacy',
    icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  },
  {
    title: 'Terms of Service',
    description: 'Terms and conditions for using VantaRust services.',
    href: '/terms',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
  {
    title: 'Imprint',
    description: 'Legal disclosure and operator information.',
    href: '/imprint',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
  {
    title: 'Legal & Copyright Claims',
    description: 'Submit legal inquiries and copyright claims.',
    href: '/legal-claims',
    icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
  },
];

export default function LegalPage() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => {
      if (sectionRef?.current) {
        observer?.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <>
      <ParticleCanvas />
      <div className="vignette" aria-hidden="true" />
      <Header />
      <main className="relative min-h-screen pt-32 pb-20 px-6" style={{ zIndex: 2 }}>
        <div 
          ref={sectionRef}
          className={`max-w-5xl mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Hero Title */}
          <h1 
            className="text-6xl md:text-7xl font-black text-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #c4beff 50%, #6a5cff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Legal
          </h1>

          {/* Subtitle */}
          <p className="text-center text-vanta-muted text-lg mb-16 max-w-2xl mx-auto">
            Access our legal documents, privacy policy, and terms of service.
          </p>

          {/* Legal Links Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {legalLinks.map((link, index) => (
              <Link
                key={link.href}
                href={link.href}
                className="group relative rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2"
                style={{
                  background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.08) 0%, rgba(139, 127, 255, 0.05) 100%)',
                  border: '2px solid rgba(106, 92, 255, 0.2)',
                  backdropFilter: 'blur(30px)',
                  boxShadow: '0 25px 80px rgba(106, 92, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  animationDelay: `${index * 100}ms`,
                }}
              >
                {/* Hover Glow Effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl"
                  style={{
                    background: 'radial-gradient(circle at 50% 50%, rgba(106, 92, 255, 0.15) 0%, transparent 70%)',
                  }}
                />

                {/* Icon */}
                <div 
                  className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110"
                  style={{
                    background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.25) 0%, rgba(139, 127, 255, 0.15) 100%)',
                    border: '2px solid rgba(106, 92, 255, 0.4)',
                    boxShadow: '0 10px 40px rgba(106, 92, 255, 0.3)',
                  }}
                >
                  <svg className="w-7 h-7" fill="none" stroke="#6a5cff" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={link.icon} />
                  </svg>
                </div>

                {/* Content */}
                <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-vanta-accent-light transition-colors">
                  {link.title}
                </h2>
                <p className="text-vanta-muted leading-relaxed">
                  {link.description}
                </p>

                {/* Arrow Icon */}
                <div className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-2">
                  <svg className="w-6 h-6" fill="none" stroke="#6a5cff" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}